import { lastNameSortKey } from "../bowlerName";
import {
  PAYOUT_CHOP,
  PAYOUT_FIRST,
  PAYOUT_REFUND,
  PAYOUT_SECOND,
  roundMoney,
} from "./constants";

export const PAYOUT_KIND_LABEL = {
  [PAYOUT_FIRST]: "1st",
  [PAYOUT_SECOND]: "2nd",
  [PAYOUT_REFUND]: "Refund",
  [PAYOUT_CHOP]: "Chop",
};

const KIND_ORDER = [PAYOUT_FIRST, PAYOUT_SECOND, PAYOUT_CHOP, PAYOUT_REFUND];

export const payoutKindLabel = (kind) => PAYOUT_KIND_LABEL[kind] || kind;

export const placesSummary = (rows) => {
  const counts = new Map();
  (rows || []).forEach((row) => {
    counts.set(row.kind, (counts.get(row.kind) || 0) + 1);
  });
  return KIND_ORDER.filter((kind) => counts.get(kind))
    .map((kind) => {
      const count = counts.get(kind);
      const label = payoutKindLabel(kind);
      return count === 1 ? label : `${count} × ${label}`;
    })
    .join(" · ");
};

export const aggregatePlaceLines = (rows) => {
  const leftovers = new Map();
  const others = [];
  (rows || []).forEach((row) => {
    if (row.kind === PAYOUT_REFUND) {
      const key = String(row.squadID || "");
      const current = leftovers.get(key) || {
        kind: PAYOUT_REFUND,
        squadID: row.squadID || null,
        treeNumber: null,
        count: 0,
        amount: 0,
        ticket: row.ticket,
      };
      current.count += 1;
      current.amount = roundMoney(
        current.amount + Number(row.amount || 0)
      );
      leftovers.set(key, current);
      return;
    }
    others.push({ ...row, count: 1, amount: Number(row.amount || 0) });
  });
  others.sort(
    (left, right) =>
      Number(left.treeNumber || 0) - Number(right.treeNumber || 0)
  );
  return [...others, ...leftovers.values()];
};

const moneySum = (rows, predicate) =>
  roundMoney(
    (rows || [])
      .filter(predicate)
      .reduce((sum, row) => sum + Number(row.amount || 0), 0)
  );

export const aggregateOwedByBowler = (rows, bowlers) => {
  const nameFor = (bowlerId) =>
    (bowlers || []).find((bowler) => bowler.id === bowlerId)?.name || "Bowler";
  const byBowler = new Map();
  (rows || []).forEach((row) => {
    const bowlerId = row.ticket?.bowlerID;
    if (!bowlerId) return;
    const current = byBowler.get(bowlerId) || {
      bowlerId,
      bowlerName: nameFor(bowlerId),
      rows: [],
    };
    current.rows.push(row);
    byBowler.set(bowlerId, current);
  });
  return [...byBowler.values()]
    .map((entry) => ({
      ...entry,
      total: moneySum(entry.rows, () => true),
      unpaid: moneySum(entry.rows, (row) => !row.paidAt),
      paid: moneySum(entry.rows, (row) => row.paidAt),
      places: placesSummary(entry.rows),
    }))
    .sort((left, right) =>
      lastNameSortKey(left.bowlerName).localeCompare(
        lastNameSortKey(right.bowlerName)
      )
    );
};

export const aggregateOwedBySquad = (rows, squads) => {
  const bySquad = new Map();
  (squads || []).forEach((squad) => {
    bySquad.set(squad.id, { squad, rows: [] });
  });
  (rows || []).forEach((row) => {
    const squadId = row.squadID;
    if (!squadId) return;
    const current = bySquad.get(squadId) || { squad: { id: squadId }, rows: [] };
    current.rows.push(row);
    bySquad.set(squadId, current);
  });
  const order = (squads || []).map((squad) => squad.id);
  return [...bySquad.values()]
    .filter((entry) => entry.rows.length)
    .sort((left, right) => {
      const leftIndex = order.indexOf(left.squad.id);
      const rightIndex = order.indexOf(right.squad.id);
      if (leftIndex !== -1 && rightIndex !== -1) return leftIndex - rightIndex;
      if (leftIndex !== -1) return -1;
      if (rightIndex !== -1) return 1;
      return 0;
    })
    .map((entry) => ({
      squadId: entry.squad.id,
      squad: entry.squad,
      rows: entry.rows,
      total: moneySum(entry.rows, () => true),
      unpaid: moneySum(entry.rows, (row) => !row.paidAt),
      paid: moneySum(entry.rows, (row) => row.paidAt),
    }));
};

export const csvCell = (value) => {
  const text = String(value ?? "");
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

export const csvFromRows = (rows) =>
  (rows || []).map((row) => row.map(csvCell).join(",")).join("\n");

export const summaryPrizeCsv = (bowlerRows) => [
  ["Bowler", "Total", "Still to pay", "Paid", "Places"],
  ...(bowlerRows || []).map((row) => [
    row.bowlerName,
    Number(row.total || 0).toFixed(2),
    Number(row.unpaid || 0).toFixed(2),
    Number(row.paid || 0).toFixed(2),
    row.places,
  ]),
];

export const detailPrizeCsv = (detailRows, squadLabel) => [
  ["Bowler", "Squad", "Tree", "Place", "Amount", "Paid"],
  ...(detailRows || []).map((row) => [
    row.bowlerName,
    squadLabel ? squadLabel(row) : "",
    row.treeNumber ? `Tree ${row.treeNumber}` : "Leftover",
    payoutKindLabel(row.kind),
    Number(row.amount || 0).toFixed(2),
    row.paidAt ? "Paid" : "",
  ]),
];
